import { type ComponentProps } from 'react'
import { type PartnerShipBannerData } from '@/features/cms/components/CMS/types'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { PromoAssetBanner } from '@shipt/design-system-banner'
import styled from 'styled-components'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { screenSizes } from '@shipt/design-system-themes'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { useAppendBranchDeepLinkParams } from '@/lib/branch/utils'

/**
 * This component corresponds to the `partnership_banner` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/partnership_banner
 * - **Production** - https://cms.shipt.com/content-type/pool/partnership_banner
 */
export const PartnershipBanner = ({
  data: {
    partner,
    partnership_logo,
    cta_text,
    bold_text,
    regular_text,
    terms_cta_text,
    variant,
    no_emphasis,
  },
}: PartnerShipBannerData) => {
  const { trackCMSElementClicked, trackCMSElementViewed } = useCMSTrackingEvents()

  const ctaUrl = useAppendBranchDeepLinkParams(cta_text?.url ?? '')

  const handleOnClick = () => {
    trackCMSElementClicked({
      type: 'link',
      next_location: ctaUrl,
      content: cta_text?.text,
    })
  }

  // Do not render the Visa partnership banner. Keeping this here just in case
  // there are instances that have not been cleaned up in the CMS.
  if (partner === 'visa') return null

  const bannerProps: Omit<ComponentProps<typeof PromoAssetBanner>, 'type'> = {
    size: 'lg',
    title: bold_text ?? '',
    variant: variant ?? 'brand',
    hasEmphasis: Boolean(no_emphasis),
    asset: <Image src={partnership_logo.src} alt={partnership_logo.alt} width={48} height={48} />,
    actionButton: {
      content: cta_text?.text ?? '',
      onClick: handleOnClick,
      variant: 'secondary',
      forwardedAs: ButtonLink,
      href: ctaUrl,
    },
    description: (
      <>
        <span id="regular_text">{regular_text}</span>{' '}
        {!!terms_cta_text && (
          <TermsLink
            aria-describedby="regular_text"
            href={terms_cta_text.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {terms_cta_text.text}
          </TermsLink>
        )}
      </>
    ),
  }

  return (
    <ScrollIntoView onInView={() => trackCMSElementViewed({ content: cta_text?.text ?? '' })}>
      <GridLayoutContainer>
        <MobileBanner {...bannerProps} type="trailing" />
        <DesktopBanner {...bannerProps} type="leading" />
      </GridLayoutContainer>
    </ScrollIntoView>
  )
}

const TermsLink = styled.a`
  color: inherit;
`

const MobileBanner = styled(PromoAssetBanner)`
  @media ${screenSizes.tablet} {
    display: none;
  }
`

const DesktopBanner = styled(PromoAssetBanner)`
  @media ${screenSizes.max_mobile} {
    display: none;
  }
`
